import { Col, Flex, Row } from "antd";
import React, { useState, useEffect } from "react";
import CardComponent from "@/components/card";
import ButtonComponent from "@/components/button/Button";
import { useDispatch } from "react-redux";
import { setCurrentForm, setFormProps, setModalOpen } from "@/reducers/commonSlice";

const TimeSlotSelector = ({
  initialTimeSlots = [],
  onUpdateAvailability,
  day,
  savedLocalSlotData,
  fetchData,
}) => {
  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [selectionRange, setSelectionRange] = useState({
    start: null,
    end: null,
  });
  const dispatch = useDispatch();

  // Generate all time slots for a day
  useEffect(() => {
    const slots = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minutes = 0; minutes < 60; minutes += 30) {
        const start = `${String(hour).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
        const endMinutes = minutes + 30;
        const endHour = hour + Math.floor(endMinutes / 60);
        const end = `${String(endHour).padStart(2, "0")}:${String(endMinutes % 60).padStart(
          2,
          "0"
        )}`;
        slots.push({ start, end, active: false });
      }
    }

    // Mark slots as active if they fall within any range in initialTimeSlots
    initialTimeSlots.forEach((range) => {
      const { start: rangeStart, end: rangeEnd } = range;

      slots.forEach((slot) => {
        if (
          (slot.start >= rangeStart && slot.start < rangeEnd) ||
          (slot.end > rangeStart && slot.end <= rangeEnd) ||
          (rangeEnd < rangeStart && // Handle crossing midnight
            (slot.start >= rangeStart || slot.end <= rangeEnd))
        ) {
          slot.active = true;
        }
      });
    });

    setTimeSlots(slots);
    setSelectedSlots(initialTimeSlots);
  }, [initialTimeSlots]);

  const toggleSlot = (index) => {
    const newSlots = [...timeSlots];
    if (newSlots[index].active) {
      newSlots[index].active = false;
      setTimeSlots(newSlots);
      setSelectedSlots(getCombinedRanges());
      return;
    }
    let { start, end } = selectionRange;
    if (start !== null && end !== null) {
      start = null;
      end = null;
      setSelectionRange({ start: null, end: null });
    }

    if (start === null) {
      // First slot selected: set the start
      setSelectionRange({ start: index, end: null });
      newSlots[index].active = true;
    } else if (end === null) {
      // Second slot selected: set the range and auto-select all slots in between
      const [startIndex, endIndex] = [start, index].sort((a, b) => a - b);

      // Set the active slots from start to end
      for (let i = startIndex; i <= endIndex; i++) {
        newSlots[i].active = true;
      }

      setSelectionRange({ start: startIndex, end: endIndex });
    } else {
      // Both start and end are set, reset the range and handle manual selection
      if (newSlots[index].active) {
        // If the slot is already active, unselect it
        newSlots[index].active = false;
      } else {
        // If the slot is not active, select it
        newSlots[index].active = true;
      }

      // Reset the selection range as we're manually toggling
      setSelectionRange({ start: null, end: null });
    }

    setTimeSlots(newSlots);
    setSelectedSlots(getCombinedRanges());
  };

  const clearRange = () => {
    setTimeSlots((prevSlots) => prevSlots.map((slot) => ({ ...slot, active: false })));
    setSelectionRange({ start: null, end: null });
    setSelectedSlots([]);
  };

  const getCombinedRanges = () => {
    const activeSlots = timeSlots.filter((slot) => slot.active);
    if (activeSlots.length === 0) return [];

    const ranges = [];
    let currentRange = { start: activeSlots[0].start, end: activeSlots[0].end };

    for (let i = 1; i < activeSlots.length; i++) {
      const prevSlot = activeSlots[i - 1];
      const currentSlot = activeSlots[i];
      if (prevSlot.end === currentSlot.start) {
        currentRange.end = currentSlot.end;
      } else {
        ranges.push(currentRange);
        currentRange = { start: currentSlot.start, end: currentSlot.end };
      }
    }
    ranges.push(currentRange);

    return ranges;
  };

  const updateAvailability = async () => {
    console.log("Selected Ranges:", selectedSlots);
    await onUpdateAvailability(selectedSlots, selectedSlots.length === 0);
    fetchData();
  };
  const handleCloneSession = () => {
    dispatch(setModalOpen(true));
    dispatch(setCurrentForm("cloneSlot"));
    dispatch(
      setFormProps({
        currentDay: day,
        currentSlot: selectedSlots,
        savedLocalSlotData,
        fetchData,
      })
    );
  };

  return (
    <div>
      <div className="date-time-picker">
        <div className="session-slots">
          <div className="slots" style={{ maxHeight: "100%" }}>
            <Row gutter={[8, 8]}>
              <Col span={24}>
                <Flex
                  gap={"5px"}
                  style={{
                    flexWrap: "wrap",
                    justifyContent: "center", // Default alignment for larger screens
                  }}
                >
                  <ButtonComponent label="Update" size="medium" onClick={updateAvailability} />
                  <ButtonComponent label="Reset" size="medium" onClick={clearRange} />
                  <ButtonComponent
                    label="Copy"
                    size="medium"
                    disabled={!timeSlots?.length}
                    onClick={handleCloneSession}
                  />
                </Flex>
              </Col>
              {timeSlots.length ? (
                timeSlots.map((slot, index) => (
                  <Col xs={12} sm={12} md={4} lg={4} key={index}>
                    <div
                      className={slot.active ? "active" : "slot"}
                      onClick={() => toggleSlot(index)}
                      style={{
                        cursor: "pointer",
                        margin: "10px",
                      }}
                    >
                      <CardComponent>{slot.start}</CardComponent>
                    </div>
                  </Col>
                ))
              ) : (
                <div style={{ textAlign: "center", width: "100%" }}>No Slot Available</div>
              )}
            </Row>
          </div>
        </div>
      </div>
      {/* <button
        onClick={updateAvailability}
        style={{
          marginTop: "20px",
          padding: "10px 20px",
          background: "blue",
          color: "white",
          border: "none",
          borderRadius: "5px",
          cursor: "pointer",
        }}
      >
        Update
      </button>
      <button
        onClick={clearRange}
        style={{
          marginTop: "10px",
          marginLeft: "10px",
          padding: "10px 20px",
          background: "red",
          color: "white",
          border: "none",
          borderRadius: "5px",
          cursor: "pointer",
        }}
      >
        Reset
      </button> */}
    </div>
  );
};

export default TimeSlotSelector;
